/**
 * Hook useAutoSave - sauvegarde automatique avec debounce
 *
 * Usage :
 *   const { status, lastSaved, save } = useAutoSave({
 *     data: formData,
 *     onSave: async (data) => api.put(`/clients/${id}`, data),
 *     enabled: clientLoaded,
 *     delay: 1500,
 *     deps: [id],
 *   });
 *
 * Affichage du status :
 *   <AutoSaveIndicator status={status} lastSaved={lastSaved} />
 */
import { useEffect, useRef, useState, useCallback } from 'react';

export function useAutoSave({ data, onSave, enabled = true, delay = 1500, deps = [] }) {
  const [status, setStatus] = useState('idle'); // idle | dirty | saving | saved | error
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const lastDataRef = useRef(JSON.stringify(data));
  const onSaveRef = useRef(onSave);
  const initialMount = useRef(true);

  // Toujours utiliser la dernière version de onSave
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);

  // Skip first mount (la donnée vient de charger, pas de sauvegarde nécessaire)
  useEffect(() => {
    initialMount.current = true;
    lastDataRef.current = JSON.stringify(data);
    setStatus('idle');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // À chaque changement de data → debounce save
  useEffect(() => {
    if (!enabled) return;
    const serialized = JSON.stringify(data);
    if (serialized === lastDataRef.current) return;

    if (initialMount.current) {
      initialMount.current = false;
      lastDataRef.current = serialized;
      return;
    }

    setStatus('dirty');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setStatus('saving');
      try {
        await onSaveRef.current(data);
        lastDataRef.current = JSON.stringify(data);
        setLastSaved(new Date());
        setStatus('saved');
        setError(null);
        setTimeout(() => {
          setStatus((s) => (s === 'saved' ? 'idle' : s));
        }, 2500);
      } catch (e) {
        setStatus('error');
        setError(e?.response?.data?.detail || e?.message || 'Erreur de sauvegarde');
      }
    }, delay);

    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data), enabled, delay]);

  // Save manuel (utile pour blur, etc.)
  const save = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    setStatus('saving');
    try {
      await onSaveRef.current(data);
      lastDataRef.current = JSON.stringify(data);
      setLastSaved(new Date());
      setStatus('saved');
      setError(null);
    } catch (e) {
      setStatus('error');
      setError(e?.response?.data?.detail || e?.message || 'Erreur de sauvegarde');
    }
  }, [data]);

  return { status, lastSaved, error, save };
}

/**
 * Composant indicateur d'auto-save (à afficher en haut à droite des formulaires)
 */
export function AutoSaveIndicator({ status, lastSaved, error, className = '' }) {
  const fmt = (d) => {
    if (!d) return '';
    const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diffSec < 5) return "à l'instant";
    if (diffSec < 60) return `il y a ${diffSec}s`;
    if (diffSec < 3600) return `il y a ${Math.floor(diffSec / 60)} min`;
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  let label = '';
  let cls = 'text-gray-400';
  let dot = '⚪';
  if (status === 'idle' && lastSaved) {
    label = `Enregistré ${fmt(lastSaved)}`;
    cls = 'text-gray-500';
    dot = '✓';
  } else if (status === 'dirty') {
    label = 'Modifications non enregistrées…';
    cls = 'text-amber-600';
    dot = '●';
  } else if (status === 'saving') {
    label = 'Enregistrement…';
    cls = 'text-blue-600';
    dot = '⟳';
  } else if (status === 'saved') {
    label = 'Enregistré ✓';
    cls = 'text-emerald-600 font-medium';
    dot = '✓';
  } else if (status === 'error') {
    label = `Erreur : ${error || 'sauvegarde impossible'}`;
    cls = 'text-rose-600';
    dot = '✗';
  }

  if (!label && status === 'idle' && !lastSaved) return null;

  return (
    <span className={`text-xs flex items-center gap-1 ${cls} ${className}`} data-testid="auto-save-indicator" data-status={status}>
      <span className={status === 'saving' ? 'animate-spin' : ''}>{dot}</span>
      <span>{label}</span>
    </span>
  );
}
